import {
  useEffect, useId, useRef, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { parseDate } from '../format';
import { useI18n } from '../i18n/I18nProvider';

const classes = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');

/** The ids and state `Field` hands its control, for a control built outside the package to take whole. */
export interface FieldControlProps {
  id: string;
  'aria-describedby': string | undefined;
  'aria-invalid': boolean | undefined;
  required: boolean | undefined;
}

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  /** Receives the ids to wire up; every input in the package is labelled through this (NFR-USE-04). */
  children: (props: FieldControlProps) => ReactNode;
}

/**
 * A labelled form control.
 *
 * The label, hint and error are wired to the input by id rather than by proximity, so a screen reader
 * reads the error with the field instead of leaving the user to hunt for it (NFR-USE-04, NFR-USE-05).
 */
export function Field({ label, hint, error, required, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="dg-field">
      <label className="dg-field__label" htmlFor={id}>
        {label}
        {required && <span className="dg-field__required" aria-hidden="true">*</span>}
      </label>
      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        required: required || undefined,
      })}
      {hint && <p className="dg-field__hint" id={hintId}>{hint}</p>}
      {/* `alert` so a validation failure is announced the moment it appears, not on the next focus move. */}
      {error && <p className="dg-field__error" id={errorId} role="alert">{error}</p>}
    </div>
  );
}

export type InputProps = InputHTMLAttributes<HTMLInputElement>;

export function Input({ className, ...rest }: InputProps) {
  return <input className={classes('dg-input', className)} {...rest} />;
}

/**
 * A phone input.
 *
 * Forced to `ltr` regardless of the page direction: a number is read left to right in both languages, and
 * an E.164 number typed into an RTL field puts its `+` on the wrong end. `inputMode="tel"` gets the phone
 * keypad rather than the full keyboard, which matters to a parent entering this once on a small screen.
 */
export function PhoneInput({ className, ...rest }: InputProps) {
  return (
    <input
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      dir="ltr"
      className={classes('dg-input', 'dg-input--phone', className)}
      {...rest}
    />
  );
}

/** An email input. Forced to `ltr`, like a phone number: an address reads left to right in both languages. */
export function EmailInput({ className, ...rest }: InputProps) {
  return <input type="email" dir="ltr" autoComplete="email" className={classes('dg-input', className)} {...rest} />;
}

/** A password input with a show/hide control, because a mistyped password on a phone is invisible. */
export function PasswordInput({ className, ...rest }: InputProps) {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);

  return (
    <div className="dg-input-group">
      <input
        type={visible ? 'text' : 'password'}
        className={classes('dg-input', className)}
        {...rest}
      />
      <button
        type="button"
        className="dg-input-group__action"
        onClick={() => setVisible((current) => !current)}
        // The control keeps one accessible name that describes what it will do next.
        aria-label={visible ? t('auth.password.hide') : t('auth.password.show')}
        aria-pressed={visible}
      >
        <span aria-hidden="true">{visible ? '🙈' : '👁️'}</span>
      </button>
    </div>
  );
}

export interface DateInputProps extends Omit<InputProps, 'type' | 'value' | 'onChange' | 'min' | 'max'> {
  /** `YYYY-MM-DD`, or '' for none. */
  value: string;
  /** `YYYY-MM-DD` once what is typed is a whole date in range, and '' while it is empty or is not one. */
  onChange: (value: string) => void;
  min?: string;
  max?: string;
}

/**
 * A date field that shows and takes `D/M/YYYY` whatever the browser's language (SRS §2.4).
 *
 * A native `type="date"` field draws its value in the order of the browser's own language, so an English
 * Chromium shows `mm/dd/yyyy`, and no attribute or style changes that. This is a text field instead. Its
 * calendar button opens the browser's own picker through a native date field kept out of sight, so a
 * phone still offers its date wheel; where the browser has no `showPicker`, the date is typed.
 *
 * What cannot be read as a date is said under the field and marked invalid for the browser's own
 * validation. A form that is `noValidate` asks `invalidDateIn(form)` before it submits, because the value it
 * holds meanwhile is '' and an optional date would otherwise go out as no date at all.
 */
export function DateInput({ value, onChange, min, max, className, id, ...rest }: DateInputProps) {
  const { t, formatDate } = useI18n();
  const shown = (iso: string) => (iso ? formatDate(iso) : '');
  const [text, setText] = useState(() => shown(value));
  // The value `text` stands for. A different one arriving from outside (a reset, a default) replaces the text.
  const [held, setHeld] = useState(value);
  if (value !== held) {
    setHeld(value);
    setText(shown(value));
  }

  const typed = parseDate(text);
  const problem = !text.trim() ? null
    : !typed ? t('date.invalid')
      : min && typed < min ? t('date.tooEarly', { date: formatDate(min) })
        : max && typed > max ? t('date.tooLate', { date: formatDate(max) })
          : null;

  const field = useRef<HTMLInputElement>(null);
  const native = useRef<HTMLInputElement>(null);
  useEffect(() => { field.current?.setCustomValidity(problem ?? ''); }, [problem]);

  const send = (next: string) => {
    if (next === held) return;
    setHeld(next);
    onChange(next);
  };

  const type = (next: string) => {
    setText(next);
    const iso = parseDate(next);
    send(iso && !(min && iso < min) && !(max && iso > max) ? iso : '');
  };

  const pick = (iso: string) => {
    setText(shown(iso));
    send(iso);
  };

  const open = () => {
    try {
      native.current?.showPicker();
    } catch {
      // A browser that will not show the picker here leaves the date to be typed.
      field.current?.focus();
    }
  };

  const errorId = id ? `${id}-date-error` : undefined;
  const canPick = typeof HTMLInputElement !== 'undefined' && 'showPicker' in HTMLInputElement.prototype;

  return (
    <>
      <div className="dg-input-group dg-date">
        <input
          ref={field}
          id={id}
          type="text"
          autoComplete="off"
          placeholder={t('date.placeholder')}
          className={classes('dg-input', className)}
          {...rest}
          value={text}
          onChange={(event) => type(event.target.value)}
          aria-invalid={problem ? true : rest['aria-invalid']}
          aria-describedby={[rest['aria-describedby'], problem ? errorId : undefined].filter(Boolean).join(' ') || undefined}
        />
        {canPick && (
          <>
            <input
              ref={native}
              type="date"
              className="dg-date__native"
              tabIndex={-1}
              aria-hidden="true"
              value={held}
              min={min}
              max={max}
              onChange={(event) => pick(event.target.value)}
            />
            <button
              type="button"
              className="dg-input-group__action"
              onClick={open}
              aria-label={t('date.pick')}
              disabled={rest.disabled}
            >
              <span aria-hidden="true">📅</span>
            </button>
          </>
        )}
      </div>
      {problem && <p className="dg-field__error" id={errorId} role="alert">{problem}</p>}
    </>
  );
}

/** The first date field in `form` whose text is not a date it accepts, for a `noValidate` form to stop on. */
export function invalidDateIn(form: HTMLFormElement): HTMLInputElement | null {
  return form.querySelector<HTMLInputElement>('.dg-date > .dg-input[aria-invalid="true"]');
}

/**
 * A new password typed twice. The caller compares the two on submit and passes the result as `mismatch`.
 */
export function NewPasswordFields({ labels, password, confirmation, onPassword, onConfirmation, mismatch }: {
  labels: { password: string; confirm: string; mismatch: string };
  password: string;
  confirmation: string;
  onPassword: (value: string) => void;
  onConfirmation: (value: string) => void;
  mismatch: boolean;
}) {
  return (
    <>
      <Field label={labels.password} required>
        {(props) => (
          <PasswordInput {...props} value={password} onChange={(event) => onPassword(event.target.value)} autoComplete="new-password" />
        )}
      </Field>
      <Field label={labels.confirm} error={mismatch ? labels.mismatch : null} required>
        {(props) => (
          <PasswordInput {...props} value={confirmation} onChange={(event) => onConfirmation(event.target.value)} autoComplete="new-password" />
        )}
      </Field>
    </>
  );
}

/** The password the person already signs in with: checked, never set. */
export function CurrentPasswordField({ label, hint, error, value, onChange }: {
  label: string;
  hint?: string;
  error?: string | null;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Field label={label} hint={hint} error={error} required>
      {(props) => (
        <PasswordInput {...props} value={value} onChange={(event) => onChange(event.target.value)} autoComplete="current-password" />
      )}
    </Field>
  );
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={classes('dg-select', className)} {...rest}>{children}</select>;
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={classes('dg-textarea', className)} {...rest} />;
}
